import React, { useState, useMemo } from "react";
import { FiPlus, FiSearch, FiCalendar, FiFilter, FiDollarSign, FiPackage, FiTrendingUp, FiX } from "react-icons/fi";
import {
  useGetMaterialPurchasesQuery,
  useCreateMaterialPurchaseMutation,
  useCancelMaterialPurchaseMutation,
  useGetMaterialPurchaseStatsQuery,
  useGetStockItemsQuery,
} from "../services/api";
import { ApiErrorHandler } from "../components/ErrorBoundary";

function formatNumber(value) {
  return new Intl.NumberFormat("en-ET", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);
}

function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString("en-ET", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function MaterialPurchasesPage() {
  const [showNewPurchaseModal, setShowNewPurchaseModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterMaterial, setFilterMaterial] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const { data: purchasesData, isLoading, error: purchasesError, refetch } = useGetMaterialPurchasesQuery({
    search: searchTerm,
    material_type: filterMaterial,
    status: filterStatus,
    from_date: fromDate,
    to_date: toDate,
  });

  const { data: statsData } = useGetMaterialPurchaseStatsQuery();

  const purchases = useMemo(() => purchasesData?.data || [], [purchasesData]);
  const stats = useMemo(() => statsData?.data || [], [statsData]);

  // Show error boundary if there's an error
  if (purchasesError) {
    return <ApiErrorHandler error={purchasesError} onRetry={refetch} />;
  }

  // Calculate totals from stats
  const goldStats = stats.find((s) => s.material_type === "gold") || {};
  const silverStats = stats.find((s) => s.material_type === "silver") || {};

  return (
    <div style={{ background: "#F7F8FA", minHeight: "100vh", padding: "24px" }}>
      {/* Page Header */}
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "start",
        marginBottom: "24px",
        flexWrap: "wrap",
        gap: "16px",
      }}>
        <div>
          <h1 style={{ fontSize: "2rem", fontWeight: 700, margin: 0, marginBottom: "8px", color: "#14213D" }}>
            💰 Material Purchases
          </h1>
          <p style={{ fontSize: "0.9375rem", color: "#64748B", margin: 0 }}>
            Buy gold and silver from walk-in customers
          </p>
        </div>

        <button
          onClick={() => setShowNewPurchaseModal(true)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "12px 24px",
            background: "linear-gradient(135deg, #F5B82E 0%, #D4A029 100%)",
            color: "white",
            border: "none",
            borderRadius: "12px",
            fontSize: "0.9375rem",
            fontWeight: 600,
            cursor: "pointer",
            boxShadow: "0 4px 12px rgba(245, 184, 46, 0.3)",
          }}
        >
          <FiPlus size={18} />
          New Purchase
        </button>
      </div>

      {/* Statistics Cards */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
        gap: "16px",
        marginBottom: "24px",
      }}>
        {/* Gold Purchases */}
        <div style={{
          background: "white",
          borderRadius: "16px",
          border: "2px solid #E2E8F0",
          padding: "24px",
        }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: "16px",
          }}>
            <div style={{
              width: "48px",
              height: "48px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #F5B82E 0%, #D4A029 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.5rem",
            }}>
              ⚜️
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: "0.8125rem", color: "#64748B", fontWeight: 600 }}>
                Gold Purchases
              </div>
              <div style={{ fontSize: "1.75rem", fontWeight: 800, color: "#14213D", lineHeight: 1 }}>
                {formatNumber(goldStats.total_weight || 0)} g
              </div>
            </div>
          </div>
          <div style={{
            paddingTop: "16px",
            borderTop: "1px solid #F1F5F9",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "12px",
          }}>
            <div>
              <div style={{ fontSize: "0.6875rem", color: "#64748B", marginBottom: "4px" }}>
                Purchases
              </div>
              <div style={{ fontSize: "1.125rem", fontWeight: 700, color: "#14213D" }}>
                {goldStats.purchase_count || 0}
              </div>
            </div>
            <div>
              <div style={{ fontSize: "0.6875rem", color: "#64748B", marginBottom: "4px" }}>
                Total Amount
              </div>
              <div style={{ fontSize: "1.125rem", fontWeight: 700, color: "#F5B82E" }}>
                {formatNumber(goldStats.total_amount || 0)} Br
              </div>
            </div>
          </div>
        </div>

        {/* Silver Purchases */}
        <div style={{
          background: "white",
          borderRadius: "16px",
          border: "2px solid #E2E8F0",
          padding: "24px",
        }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: "16px",
          }}>
            <div style={{
              width: "48px",
              height: "48px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #94A3B8 0%, #64748B 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.5rem",
            }}>
              🪙
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: "0.8125rem", color: "#64748B", fontWeight: 600 }}>
                Silver Purchases
              </div>
              <div style={{ fontSize: "1.75rem", fontWeight: 800, color: "#14213D", lineHeight: 1 }}>
                {formatNumber(silverStats.total_weight || 0)} g
              </div>
            </div>
          </div>
          <div style={{
            paddingTop: "16px",
            borderTop: "1px solid #F1F5F9",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "12px",
          }}>
            <div>
              <div style={{ fontSize: "0.6875rem", color: "#64748B", marginBottom: "4px" }}>
                Purchases
              </div>
              <div style={{ fontSize: "1.125rem", fontWeight: 700, color: "#14213D" }}>
                {silverStats.purchase_count || 0}
              </div>
            </div>
            <div>
              <div style={{ fontSize: "0.6875rem", color: "#64748B", marginBottom: "4px" }}>
                Total Amount
              </div>
              <div style={{ fontSize: "1.125rem", fontWeight: 700, color: "#64748B" }}>
                {formatNumber(silverStats.total_amount || 0)} Br
              </div>
            </div>
          </div>
        </div>

        {/* Average Rate */}
        <div style={{
          background: "white",
          borderRadius: "16px",
          border: "2px solid #E2E8F0",
          padding: "24px",
        }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: "16px",
          }}>
            <div style={{
              width: "48px",
              height: "48px",
              borderRadius: "12px",
              background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
              <FiTrendingUp size={24} color="white" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: "0.8125rem", color: "#64748B", fontWeight: 600 }}>
                Avg Gold Rate
              </div>
              <div style={{ fontSize: "1.75rem", fontWeight: 800, color: "#14213D", lineHeight: 1 }}>
                {formatNumber(goldStats.avg_rate || 0)}
              </div>
            </div>
          </div>
          <div style={{
            paddingTop: "16px",
            borderTop: "1px solid #F1F5F9",
          }}>
            <div style={{ fontSize: "0.6875rem", color: "#64748B", marginBottom: "4px" }}>
              Avg Silver Rate
            </div>
            <div style={{ fontSize: "1.125rem", fontWeight: 700, color: "#64748B" }}>
              {formatNumber(silverStats.avg_rate || 0)} Br/g
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div style={{
        background: "white",
        borderRadius: "12px",
        border: "2px solid #E2E8F0",
        padding: "16px",
        marginBottom: "16px",
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: "12px",
        alignItems: "end",
      }}>
        {/* Search */}
        <div style={{ gridColumn: "span 2", minWidth: "300px", position: "relative" }}>
          <FiSearch
            size={18}
            style={{
              position: "absolute",
              left: "12px",
              top: "50%",
              transform: "translateY(-50%)",
              color: "#94A3B8",
            }}
          />
          <input
            type="text"
            placeholder="Search by customer name, phone, or purchase #..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: "100%",
              padding: "10px 12px 10px 40px",
              border: "1px solid #E2E8F0",
              borderRadius: "8px",
              fontSize: "0.875rem",
            }}
          />
        </div>

        {/* Material Filter */}
        <select
          value={filterMaterial}
          onChange={(e) => setFilterMaterial(e.target.value)}
          style={{
            padding: "10px 12px",
            border: "1px solid #E2E8F0",
            borderRadius: "8px",
            fontSize: "0.875rem",
            background: "white",
          }}
        >
          <option value="">All Materials</option>
          <option value="gold">Gold</option>
          <option value="silver">Silver</option>
        </select>

        {/* Status Filter */}
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={{
            padding: "10px 12px",
            border: "1px solid #E2E8F0",
            borderRadius: "8px",
            fontSize: "0.875rem",
            background: "white",
          }}
        >
          <option value="">All Status</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>

        {/* From Date Filter */}
        <div>
          <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 600, color: "#64748B", marginBottom: "4px" }}>
            From Date
          </label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{
              width: "100%",
              padding: "10px 12px",
              border: "1px solid #E2E8F0",
              borderRadius: "8px",
              fontSize: "0.875rem",
            }}
          />
        </div>

        {/* To Date Filter */}
        <div>
          <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 600, color: "#64748B", marginBottom: "4px" }}>
            To Date
          </label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{
              width: "100%",
              padding: "10px 12px",
              border: "1px solid #E2E8F0",
              borderRadius: "8px",
              fontSize: "0.875rem",
            }}
          />
        </div>

        {/* Clear Filters Button */}
        {(fromDate || toDate || searchTerm || filterMaterial || filterStatus) && (
          <button
            onClick={() => {
              setFromDate("");
              setToDate("");
              setSearchTerm("");
              setFilterMaterial("");
              setFilterStatus("");
            }}
            style={{
              padding: "10px 16px",
              border: "1px solid #E2E8F0",
              background: "white",
              borderRadius: "8px",
              fontSize: "0.875rem",
              fontWeight: 600,
              cursor: "pointer",
              color: "#64748B",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <FiX size={16} />
            Clear
          </button>
        )}
      </div>

      {/* Purchases Table */}
      <div style={{
        background: "white",
        borderRadius: "12px",
        border: "2px solid #E2E8F0",
        overflow: "hidden",
      }}>
        {isLoading ? (
          <div style={{ padding: "48px", textAlign: "center" }}>
            <div className="premium-spinner" />
          </div>
        ) : purchases.length === 0 ? (
          <div style={{ padding: "48px", textAlign: "center", color: "#64748B" }}>
            No purchases found
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#F9FAFB", borderBottom: "2px solid #E2E8F0" }}>
                  <th style={{
                    padding: "16px",
                    textAlign: "left",
                    fontSize: "0.8125rem",
                    fontWeight: 700,
                    color: "#14213D",
                    textTransform: "uppercase",
                  }}>
                    Purchase #
                  </th>
                  <th style={{
                    padding: "16px",
                    textAlign: "left",
                    fontSize: "0.8125rem",
                    fontWeight: 700,
                    color: "#14213D",
                    textTransform: "uppercase",
                  }}>
                    Customer
                  </th>
                  <th style={{
                    padding: "16px",
                    textAlign: "left",
                    fontSize: "0.8125rem",
                    fontWeight: 700,
                    color: "#14213D",
                    textTransform: "uppercase",
                  }}>
                    Material
                  </th>
                  <th style={{
                    padding: "16px",
                    textAlign: "right",
                    fontSize: "0.8125rem",
                    fontWeight: 700,
                    color: "#14213D",
                    textTransform: "uppercase",
                  }}>
                    Weight
                  </th>
                  <th style={{
                    padding: "16px",
                    textAlign: "right",
                    fontSize: "0.8125rem",
                    fontWeight: 700,
                    color: "#14213D",
                    textTransform: "uppercase",
                  }}>
                    Rate/g
                  </th>
                  <th style={{
                    padding: "16px",
                    textAlign: "right",
                    fontSize: "0.8125rem",
                    fontWeight: 700,
                    color: "#14213D",
                    textTransform: "uppercase",
                  }}>
                    Total
                  </th>
                  <th style={{
                    padding: "16px",
                    textAlign: "center",
                    fontSize: "0.8125rem",
                    fontWeight: 700,
                    color: "#14213D",
                    textTransform: "uppercase",
                  }}>
                    Status
                  </th>
                  <th style={{
                    padding: "16px",
                    textAlign: "left",
                    fontSize: "0.8125rem",
                    fontWeight: 700,
                    color: "#14213D",
                    textTransform: "uppercase",
                  }}>
                    Recorded By
                  </th>
                  <th style={{
                    padding: "16px",
                    textAlign: "left",
                    fontSize: "0.8125rem",
                    fontWeight: 700,
                    color: "#14213D",
                    textTransform: "uppercase",
                  }}>
                    Date
                  </th>
                </tr>
              </thead>
              <tbody>
                {purchases.map((purchase) => (
                  <tr
                    key={purchase.id}
                    style={{ borderBottom: "1px solid #F1F5F9" }}
                  >
                    <td style={{ padding: "16px", fontSize: "0.875rem", fontWeight: 600, color: "#14213D" }}>
                      {purchase.purchase_number}
                    </td>
                    <td style={{ padding: "16px" }}>
                      <div style={{ fontSize: "0.875rem", fontWeight: 600, color: "#14213D" }}>
                        {purchase.customer_name}
                      </div>
                      {purchase.customer_phone && (
                        <div style={{ fontSize: "0.75rem", color: "#64748B" }}>
                          {purchase.customer_phone}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "16px" }}>
                      <span style={{
                        padding: "4px 10px",
                        background: purchase.material_type === "gold" ? "#FEF3C7" : "#E0E7FF",
                        color: purchase.material_type === "gold" ? "#92400E" : "#3730A3",
                        borderRadius: "12px",
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                      }}>
                        {purchase.material_type === "gold" ? "⚜️" : "🪙"}
                        {purchase.material_type.toUpperCase()}
                        {purchase.purity && ` ${purchase.purity}`}
                      </span>
                    </td>
                    <td style={{
                      padding: "16px",
                      textAlign: "right",
                      fontSize: "0.9375rem",
                      fontWeight: 700,
                      color: "#14213D",
                    }}>
                      {formatNumber(purchase.weight_grams)} g
                    </td>
                    <td style={{
                      padding: "16px",
                      textAlign: "right",
                      fontSize: "0.875rem",
                      fontWeight: 600,
                      color: "#64748B",
                    }}>
                      {formatNumber(purchase.rate_per_gram)}
                    </td>
                    <td style={{
                      padding: "16px",
                      textAlign: "right",
                      fontSize: "1rem",
                      fontWeight: 700,
                      color: "#F5B82E",
                    }}>
                      {formatNumber(purchase.total_amount)} Br
                    </td>
                    <td style={{ padding: "16px", textAlign: "center" }}>
                      <span style={{
                        padding: "4px 12px",
                        background: purchase.status === "completed" ? "#D1FAE5" : "#FEE2E2",
                        color: purchase.status === "completed" ? "#047857" : "#DC2626",
                        borderRadius: "12px",
                        fontSize: "0.75rem",
                        fontWeight: 700,
                      }}>
                        {purchase.status === "completed" ? "✓ Completed" : "✗ Cancelled"}
                      </span>
                    </td>
                    <td style={{ padding: "16px", fontSize: "0.8125rem", color: "#64748B" }}>
                      {purchase.first_name && purchase.last_name
                        ? `${purchase.first_name} ${purchase.last_name}`
                        : "N/A"}
                    </td>
                    <td style={{ padding: "16px", fontSize: "0.8125rem", color: "#64748B" }}>
                      {formatDate(purchase.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* New Purchase Modal */}
      {showNewPurchaseModal && (
        <NewPurchaseModal onClose={() => setShowNewPurchaseModal(false)} />
      )}
    </div>
  );
}

// New Purchase Modal Component
function NewPurchaseModal({ onClose }) {
  const [createPurchase, { isLoading: isCreating }] = useCreateMaterialPurchaseMutation();
  const { data: stockItemsData } = useGetStockItemsQuery();
  const stockItems = useMemo(() => stockItemsData?.data || [], [stockItemsData]);

  const [formData, setFormData] = useState({
    customer_name: "",
    customer_phone: "",
    customer_id_card: "",
    material_type: "gold",
    weight_grams: "",
    purity: "",
    test_method: "",
    rate_per_gram: "",
    payment_method: "cash",
    notes: "",
    add_to_stock: true,
    stock_item_id: "",
  });

  const totalAmount = useMemo(() => {
    const weight = parseFloat(formData.weight_grams) || 0;
    const rate = parseFloat(formData.rate_per_gram) || 0;
    return weight * rate;
  }, [formData.weight_grams, formData.rate_per_gram]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await createPurchase(formData).unwrap();
      alert("Material purchase recorded successfully!");
      onClose();
    } catch (error) {
      console.error("Failed to create purchase:", error);
      alert("Failed to record purchase: " + (error.data?.message || error.message));
    }
  };

  return (
    <div style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0, 0, 0, 0.6)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1000,
      padding: "24px",
    }}>
      <div style={{
        background: "white",
        borderRadius: "16px",
        maxWidth: "700px",
        width: "100%",
        maxHeight: "90vh",
        overflow: "auto",
      }}>
        {/* Modal Header */}
        <div style={{
          padding: "24px",
          borderBottom: "2px solid #E2E8F0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}>
          <h2 style={{ fontSize: "1.5rem", fontWeight: 700, color: "#14213D", margin: 0 }}>
            💰 New Material Purchase
          </h2>
          <button
            onClick={onClose}
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "8px",
              border: "1px solid #E2E8F0",
              background: "transparent",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FiX size={18} />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} style={{ padding: "24px" }}>
          <div style={{ display: "grid", gap: "20px" }}>
            {/* Customer Information */}
            <div style={{
              padding: "16px",
              background: "#F9FAFB",
              borderRadius: "12px",
              border: "1px solid #E2E8F0",
            }}>
              <h3 style={{ fontSize: "0.9375rem", fontWeight: 700, color: "#14213D", marginBottom: "16px" }}>
                Customer Information
              </h3>
              <div style={{ display: "grid", gap: "12px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "0.8125rem", fontWeight: 600, color: "#64748B", marginBottom: "6px" }}>
                    Customer Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: "1px solid #E2E8F0",
                      borderRadius: "8px",
                      fontSize: "0.875rem",
                    }}
                  />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "0.8125rem", fontWeight: 600, color: "#64748B", marginBottom: "6px" }}>
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={formData.customer_phone}
                      onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        border: "1px solid #E2E8F0",
                        borderRadius: "8px",
                        fontSize: "0.875rem",
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "0.8125rem", fontWeight: 600, color: "#64748B", marginBottom: "6px" }}>
                      ID Card Number
                    </label>
                    <input
                      type="text"
                      value={formData.customer_id_card}
                      onChange={(e) => setFormData({ ...formData, customer_id_card: e.target.value })}
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        border: "1px solid #E2E8F0",
                        borderRadius: "8px",
                        fontSize: "0.875rem",
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Material Details */}
            <div style={{
              padding: "16px",
              background: "#F9FAFB",
              borderRadius: "12px",
              border: "1px solid #E2E8F0",
            }}>
              <h3 style={{ fontSize: "0.9375rem", fontWeight: 700, color: "#14213D", marginBottom: "16px" }}>
                Material Details
              </h3>
              <div style={{ display: "grid", gap: "12px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "0.8125rem", fontWeight: 600, color: "#64748B", marginBottom: "6px" }}>
                      Material Type *
                    </label>
                    <select
                      required
                      value={formData.material_type}
                      onChange={(e) => setFormData({ ...formData, material_type: e.target.value })}
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        border: "1px solid #E2E8F0",
                        borderRadius: "8px",
                        fontSize: "0.875rem",
                        background: "white",
                      }}
                    >
                      <option value="gold">⚜️ Gold</option>
                      <option value="silver">🪙 Silver</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "0.8125rem", fontWeight: 600, color: "#64748B", marginBottom: "6px" }}>
                      Purity
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., 22K, 18K, 925"
                      value={formData.purity}
                      onChange={(e) => setFormData({ ...formData, purity: e.target.value })}
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        border: "1px solid #E2E8F0",
                        borderRadius: "8px",
                        fontSize: "0.875rem",
                      }}
                    />
                  </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                  <div>
                    <label style={{ display: "block", fontSize: "0.8125rem", fontWeight: 600, color: "#64748B", marginBottom: "6px" }}>
                      Weight (grams) *
                    </label>
                    <input
                      type="number"
                      step="0.001"
                      required
                      value={formData.weight_grams}
                      onChange={(e) => setFormData({ ...formData, weight_grams: e.target.value })}
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        border: "1px solid #E2E8F0",
                        borderRadius: "8px",
                        fontSize: "0.875rem",
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: "block", fontSize: "0.8125rem", fontWeight: 600, color: "#64748B", marginBottom: "6px" }}>
                      Rate per Gram (Br) *
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      value={formData.rate_per_gram}
                      onChange={(e) => setFormData({ ...formData, rate_per_gram: e.target.value })}
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        border: "1px solid #E2E8F0",
                        borderRadius: "8px",
                        fontSize: "0.875rem",
                      }}
                    />
                  </div>
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "0.8125rem", fontWeight: 600, color: "#64748B", marginBottom: "6px" }}>
                    Test Method
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., Acid Test, XRF Machine"
                    value={formData.test_method}
                    onChange={(e) => setFormData({ ...formData, test_method: e.target.value })}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: "1px solid #E2E8F0",
                      borderRadius: "8px",
                      fontSize: "0.875rem",
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Payment & Stock */}
            <div style={{
              padding: "16px",
              background: "#F9FAFB",
              borderRadius: "12px",
              border: "1px solid #E2E8F0",
            }}>
              <h3 style={{ fontSize: "0.9375rem", fontWeight: 700, color: "#14213D", marginBottom: "16px" }}>
                Payment & Stock
              </h3>
              <div style={{ display: "grid", gap: "12px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "0.8125rem", fontWeight: 600, color: "#64748B", marginBottom: "6px" }}>
                    Payment Method *
                  </label>
                  <select
                    required
                    value={formData.payment_method}
                    onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: "1px solid #E2E8F0",
                      borderRadius: "8px",
                      fontSize: "0.875rem",
                      background: "white",
                    }}
                  >
                    <option value="cash">Cash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="mobile_money">Mobile Money</option>
                  </select>
                </div>
                <div>
                  <label style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    color: "#14213D",
                    cursor: "pointer",
                  }}>
                    <input
                      type="checkbox"
                      checked={formData.add_to_stock}
                      onChange={(e) => setFormData({ ...formData, add_to_stock: e.target.checked })}
                      style={{ width: "18px", height: "18px", cursor: "pointer" }}
                    />
                    Automatically add to stock inventory
                  </label>
                </div>
                <div>
                  <label style={{ display: "block", fontSize: "0.8125rem", fontWeight: 600, color: "#64748B", marginBottom: "6px" }}>
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: "1px solid #E2E8F0",
                      borderRadius: "8px",
                      fontSize: "0.875rem",
                      fontFamily: "inherit",
                      resize: "vertical",
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Total Amount Display */}
            <div style={{
              padding: "20px",
              background: "linear-gradient(135deg, #F5B82E 0%, #D4A029 100%)",
              borderRadius: "12px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}>
              <div style={{ color: "white", fontSize: "0.9375rem", fontWeight: 600 }}>
                Total Amount to Pay:
              </div>
              <div style={{ color: "white", fontSize: "2rem", fontWeight: 800 }}>
                {formatNumber(totalAmount)} Br
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div style={{
            marginTop: "24px",
            display: "flex",
            gap: "12px",
            justifyContent: "flex-end",
          }}>
            <button
              type="button"
              onClick={onClose}
              disabled={isCreating}
              style={{
                padding: "12px 24px",
                border: "1px solid #E2E8F0",
                background: "white",
                borderRadius: "8px",
                fontSize: "0.875rem",
                fontWeight: 600,
                cursor: "pointer",
                color: "#64748B",
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isCreating}
              style={{
                padding: "12px 24px",
                border: "none",
                background: "linear-gradient(135deg, #F5B82E 0%, #D4A029 100%)",
                color: "white",
                borderRadius: "8px",
                fontSize: "0.875rem",
                fontWeight: 600,
                cursor: isCreating ? "not-allowed" : "pointer",
                opacity: isCreating ? 0.6 : 1,
              }}
            >
              {isCreating ? "Recording..." : "Record Purchase"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
